<script setup lang="ts">
import ProgressSpinner from 'primevue/progressspinner';
import { computed, type Component } from 'vue';

export type UiIconTextActionLayout = 'stacked' | 'inline';

export interface UiIconTextActionButton {
  severity?: 'primary' | 'secondary' | 'success' | 'info' | 'warn' | 'danger';
  label: string;
  disabled?: boolean;
  iconPosition?: 'left' | 'right' | 'top' | 'bottom';
  icon?: string;
  customIcon?: Component;
}

interface Props {
  title: string;
  subtitle: string;
  iconComponent?: Component;
  iconProps?: object;
  button?: UiIconTextActionButton;
  buttonLayout?: UiIconTextActionLayout;
  loading?: boolean;
}

defineOptions({ name: 'UiIconTextAction' });

const {
  iconComponent = undefined,
  iconProps = undefined,
  button = undefined,
  buttonLayout = 'stacked',
  loading = false,
} = defineProps<Props>();

const buttonIconPosition = computed(() => {
  return button?.iconPosition === 'right' ? 'p-button-icon-right' : 'p-button-icon-left';
});

const emit = defineEmits<{
  action: [];
}>();
</script>

<template>
  <div class="ui-icon-text-action" :class="`ui-icon-text-action--${buttonLayout}`">
    <div v-if="iconComponent" class="ui-icon-text-action__icon">
      <component :is="iconComponent" v-bind="iconProps" />
    </div>

    <div class="ui-icon-text-action__content">
      <div class="ui-icon-text-action__text">
        <h6 class="ui-icon-text-action__title">{{ title }}</h6>
        <p class="ui-icon-text-action__subtitle">{{ subtitle }}</p>
      </div>

      <ProgressSpinner
        v-if="loading"
        class="ui-icon-text-action__spinner"
        stroke-width="6"
        aria-label="Loading"
      />

      <Button
        v-else-if="button"
        :severity="button.severity || 'primary'"
        :label="button.label"
        :disabled="button.disabled"
        :icon="button.customIcon ? undefined : button.icon"
        :icon-pos="button.iconPosition"
        class="ui-icon-text-action__button"
        rounded
        size="small"
        @click="emit('action')"
      >
        <template #icon>
          <span
            v-if="button.customIcon && !button.icon"
            class="p-button-icon"
            :class="buttonIconPosition"
          >
            <component :is="button.customIcon" />
          </span>
        </template>
      </Button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.ui-icon-text-action__icon {
  width: 2rem;
}
.ui-icon-text-action {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  width: 100%;
}

.ui-icon-text-action__content {
  flex: 1 1 auto;
  min-width: 0;
}

.ui-icon-text-action__text {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.ui-icon-text-action__spinner {
  width: $static-icon-xs;
  height: $static-icon-xs;
}

.ui-icon-text-action--stacked .ui-icon-text-action__button,
.ui-icon-text-action--stacked .ui-icon-text-action__spinner {
  margin-top: 0.5rem;
}

.ui-icon-text-action--stacked .ui-icon-text-action__spinner {
  display: block;
  margin-inline: auto;
}

.ui-icon-text-action--inline .ui-icon-text-action__content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
}

.ui-icon-text-action__title {
  margin: 0;
  color: #fff;
  font-size: 1rem; /* 16px */
  font-weight: $font-semibold;
  line-height: 1;
  letter-spacing: -0.064px;
}

.ui-icon-text-action__subtitle {
  margin: 0;
  color: #fff;
  font-size: 0.75rem; /* 12px */
  font-weight: $font-regular;
  line-height: 1;
}

.ui-icon-text-action__button {
  height: 1.625rem; /* 26px */
  padding: 0.390625rem 0.609375rem; /* 6.25px 9.75px */
  font-size: 0.75rem; /* 12px */
  font-weight: $font-medium;
  line-height: 1;
  letter-spacing: -0.048px;
  flex: 0 0 auto;
  justify-self: flex-end;
}

.ui-icon-text-action__button :deep(.p-button-label) {
  font-size: 0.75rem; /* 12px */
  font-weight: $font-medium;
  line-height: 1;
  letter-spacing: -0.048px;
}
</style>
